import type { SegmentDefinitionDto } from '@carbonwebmail/contracts'
import { count } from '../../ui/format.js'

/**
 * What a segment asks, in a sentence.
 *
 * The list shows this instead of a rule count, because "3 rules" tells you
 * nothing about whether it is the audience you meant — and the moment that
 * matters is picking one for a campaign, when opening each to check is exactly
 * what nobody does.
 *
 * Deliberately not a full rendering of the tree. A long definition is trimmed
 * with "and 2 more", because a row that wraps to four lines is a row nobody
 * reads either.
 */

/** One word per field, so a sentence reads rather than echoing an enum. */
const FIELD_WORDS: Record<string, string> = {
  status: 'state',
  suppression: 'suppression',
  name: 'name',
  email: 'email',
  company: 'company',
  lastSentDays: 'last sent',
}

/** How many rules are spelled out before the rest become a count. */
const SHOWN = 3

export function describeSegment(definition: SegmentDefinitionDto): string {
  const phrases = definition.children.map(describeChild)
  if (phrases.length === 0) return 'everyone'

  const joiner = definition.match === 'all' ? ' and ' : ' or '
  const shown = phrases.slice(0, SHOWN).join(joiner)
  const rest = phrases.length - SHOWN

  return rest > 0 ? `${shown}, and ${count(rest)} more` : shown
}

function describeChild(child: SegmentDefinitionDto['children'][number]): string {
  if ('any' in child) {
    const inner = child.any.map(describeRule).join(' or ')
    return inner === '' ? 'anything' : `(${inner})`
  }
  return describeRule(child)
}

function describeRule(rule: { field: string; operator: string; value: string }): string {
  const field = FIELD_WORDS[rule.field] ?? rule.field

  if (rule.field === 'lastSentDays') {
    const days = rule.value === '1' ? 'a day' : `${rule.value} days`
    return rule.operator === 'at_most' ? `sent within ${days}` : `not sent for ${days}`
  }

  const negated = rule.operator === 'is_not'
  return `${field} ${negated ? 'is not' : 'is'} ${rule.value.replace('_', ' ')}`
}

/** The same counting the builder uses, so a list and a preview never disagree. */
export function reachLabel(reach: number): string {
  return reach === 1 ? '1 contact' : `${count(reach)} contacts`
}
